const configuredDirectusUrl = import.meta.env.VITE_DIRECTUS_URL || import.meta.env.DIRECTUS_URL || "";
const directusToken =
  import.meta.env.VITE_DIRECTUS_STATIC_TOKEN || import.meta.env.DIRECTUS_STATIC_TOKEN || "";

const directusUrl = getSafeDirectusUrl(configuredDirectusUrl);

function normalizeBaseUrl(url) {
  return url.replace(/\/+$/, "");
}

function getSafeDirectusUrl(url) {
  if (!url) return "";
  if (typeof window === "undefined") return url;

  const pageHost = window.location.hostname;
  const isLocalPage = ["localhost", "127.0.0.1", "::1"].includes(pageHost);
  if (isLocalPage) return url;

  try {
    const { hostname } = new URL(url);
    if (isLocalOrPrivateHost(hostname)) return "";
  } catch {
    return "";
  }

  return url;
}

function isLocalOrPrivateHost(hostname) {
  const host = hostname.toLowerCase();
  if (["localhost", "127.0.0.1", "::1"].includes(host) || host.endsWith(".local")) return true;

  const parts = host.split(".").map(Number);
  if (parts.length !== 4 || parts.some((part) => Number.isNaN(part))) return false;

  const [a, b] = parts;
  return a === 10 || a === 127 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
}

async function directusRequest(path, params = {}) {
  const result = await directusRequestResult(path, params);
  return result?.data ?? null;
}

async function directusRequestResult(path, params = {}) {
  if (!directusUrl) return null;

  const url = new URL(`${normalizeBaseUrl(directusUrl)}/items/${path}`);
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, value);
    }
  }

  try {
    const response = await fetch(url, {
      headers: directusToken ? { Authorization: `Bearer ${directusToken}` } : {}
    });

    if (!response.ok) return null;
    const json = await response.json();
    return { data: json.data ?? null, meta: json.meta ?? null };
  } catch {
    return null;
  }
}

export async function getDirectusPage(slug, locale) {
  const filter = JSON.stringify({
    slug: { _eq: slug },
    locale: { _eq: locale }
  });

  const pages = await directusRequest("pages", {
    filter,
    fields:
      "slug,locale,title,description,eyebrow,body,hero_title,hero_text,cta_label,cta_href,seo_title,seo_description"
  });

  const page = Array.isArray(pages) ? pages[0] ?? null : null;
  if (!page) return null;
  return { ...page, body: await getContentBody("pages", slug, locale, page.body) };
}

export async function getDirectusArticle(slug, locale) {
  const filter = JSON.stringify({
    slug: { _eq: slug },
    locale: { _eq: locale }
  });

  const articles = await directusRequest("articles", {
    filter,
    fields: "slug,locale,title,description,excerpt,body,hero_image,seo_title,seo_description"
  });

  const article = Array.isArray(articles) ? articles[0] ?? null : null;
  if (!article) return null;
  const body = article.body?.trim()
    ? article.body
    : await getContentBody("articles", slug, locale, "");
  return { ...article, body };
}

export async function getDirectusArticleList(locale, { limit = 10, page = 1 } = {}) {
  const filter = JSON.stringify({ locale: { _eq: locale } });
  const offset = Math.max(0, page - 1) * limit;
  const result = await directusRequestResult("articles", {
    filter,
    sort: "sort,title",
    limit,
    offset,
    meta: "filter_count",
    fields: "slug,locale,route,title,description,excerpt,body,hero_image"
  });
  return {
    items: Array.isArray(result?.data) ? result.data : [],
    total: Number(result?.meta?.filter_count || 0)
  };
}

export async function getDirectusCollection(collection, locale) {
  const filter = JSON.stringify({
    locale: { _eq: locale }
  });

  const items = await directusRequest(collection, {
    filter,
    sort: "sort,title",
    fields: "slug,locale,title,description,excerpt,category,href,cta_label,sort"
  });

  return Array.isArray(items) ? items : [];
}

async function getContentBody(ownerCollection, slug, locale, fallbackBody = "") {
  const filter = JSON.stringify({
    owner_collection: { _eq: ownerCollection },
    slug: { _eq: slug },
    locale: { _eq: locale }
  });
  const blocks = await directusRequest("content_blocks", {
    filter,
    sort: "chunk_index",
    fields: "chunk_index,content",
    limit: -1
  });

  if (!Array.isArray(blocks) || blocks.length === 0) return fallbackBody || "";
  return blocks.map((block) => block.content || "").join("");
}
